import { AmountSuperScript } from "components/design/AmountSuperScript";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { Divider } from "components/design/Divider";
import Slider from "components/DesignSystem/Slider/Slider";
import { EditOpeningBalance } from "components/EditOpeningBalance/EditOpeningBalance";
import { PencilWithLine } from "components/icons/PencilWithLine";
import { LockDateToolTipContent } from "components/LockedDatePicker/LockedDatePicker";
import { LockedUi } from "components/LockedUi/LockedUi";
import { BANK_ACCOUNT_STATUS } from "constants/dataSource";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { ConditionalLink } from "components/conditionalLink";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";

import { useGetCurrenciesQuery } from "store/apis/invoices";
import { AssignCurrencyModal } from "./AssignCurrencyModal";
import { useEditBankAccountMutation } from "store/apis/bankConnections";
import { ConnectionBankAccount, Connection } from "store/apis/booksConnections";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { BackendError } from "types/utils/error";
import { formatDate } from "utils/formatDate";
import { BankStatementSlider } from "./BankStatementSlider";
import { MarkAccountCloseModal } from "./MarkAccountCloseModal";
import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";

export const BankAccountItem = ({
  account,
  connection,
}: {
  account: ConnectionBankAccount;
  connection: Connection;
}) => {
  const hasOpeningBalance =
    account.opening_balance || account.opening_balance === 0;
  const { alertToast, successToast } = useToast();
  const { link } = useConstructInternalLink();

  const accountEdit = useModal();
  const markaccountCloseModal = useModal();
  const assignCurrencyModal = useModal();
  const { uuid: groupId, secondary_channel_url } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const [editBankAccount, { isLoading: isActivating }] =
    useEditBankAccountMutation();

  const { data: currenciesResponse = {} } = useGetCurrenciesQuery();

  const currencyLabel = account.currency
    ? `${account.currency} - ${currenciesResponse[account.currency] ?? ""}`
    : null;

  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId }
  );

  const markAccountActive = async () => {
    try {
      await editBankAccount({
        bankAccountId: account.uuid,
        bankId: connection.uuid,
        groupId,
        entityId,
        payload: {
          account_status: BANK_ACCOUNT_STATUS.ACTIVE,
        },
      }).unwrap();
      successToast({
        message: "Account has been marked as active",
      });
    } catch (error) {
      alertToast({
        message: (error as BackendError).data?.error?.message,
      });
    }
  };

  return (
    <>
      <Accordion.Item value={account.uuid} key={account.uuid}>
        <Accordion.Trigger>
          <div className="t-flex t-flex-col t-gap-1 t-w-full">
            <div className="t-flex t-gap-1.5 t-items-center t-text-body-sm t-w-full t-text-text-60">
              <span>
                {account.name} {account.account_number}
              </span>

              {account.is_new_bank_account && (
                <Tag tagType="purple" size="small" rounded>
                  New
                </Tag>
              )}
            </div>
            {account.status === BANK_ACCOUNT_STATUS.CLOSED &&
              account.closed_on && (
                <Tag tagType="gray" rounded icon={false}>
                  Closed as of {dayjs(account.closed_on).format(DISPLAY_DATE)}
                </Tag>
              )}
          </div>
        </Accordion.Trigger>
        <Accordion.Content className="t-flex t-flex-col t-gap-4">
          <div className="t-grid t-grid-cols-2 t-gap-4">
            {account.status === BANK_ACCOUNT_STATUS.CLOSED &&
            account.closing_balance ? (
              <div>
                <p className="t-text-text-30 t-text-body-sm t-m-0">
                  Closing account balance
                </p>
                <p className="t-text-text-100 t-text-body-sm t-m-0">
                  <AmountSuperScript amount={account.closing_balance} />
                </p>
              </div>
            ) : (
              <div>
                <p className="t-text-text-30 t-text-body-sm t-m-0">
                  Account balance
                </p>
                <p className="t-text-text-100 t-text-body-sm t-m-0">
                  <AmountSuperScript amount={account.available_balance} />
                </p>
              </div>
            )}

            <div>
              <p className="t-text-text-30 t-text-body-sm t-m-0">
                Account Currency
              </p>
              <p className="t-text-text-100 t-text-body-sm t-m-0">
                {account.currency ? (
                  <div className="t-flex t-gap-1 t-items-center">
                    <span className="t-line-clamp-1 t-truncate">
                      {currencyLabel}
                    </span>
                    <LockedUi feature={WRITE_PERMISSION_KEYS.DATA_SOURCES}>
                      <span>
                        <ConditionalToolTip
                          condition={
                            account.is_txn_fetched && (
                              <span>
                                Account currency can&apos;t be modified due to
                                existing transactions.
                                <br /> For changes,{" "}
                                <ConditionalLink
                                  className="t-underline"
                                  to={link(
                                    `/books/chat/${secondary_channel_url}`
                                  )}
                                >
                                  <Button customType="link" size="small">
                                    <span className="t-text-text t-underline">
                                      contact support
                                    </span>
                                  </Button>
                                </ConditionalLink>
                                .
                              </span>
                            )
                          }
                          propClass="t-w-64 !t-text-left"
                        >
                          <span>
                            <Button
                              size="xsmall"
                              customType="ghost_icon"
                              onClick={assignCurrencyModal.open}
                              disabled={account.is_txn_fetched}
                            >
                              <span className="t-flex">
                                <PencilWithLine />
                              </span>
                            </Button>
                          </span>
                        </ConditionalToolTip>
                      </span>
                    </LockedUi>
                  </div>
                ) : (
                  <LockedUi feature={WRITE_PERMISSION_KEYS.DATA_SOURCES}>
                    <Button
                      size="small"
                      customType="link"
                      onClick={assignCurrencyModal.open}
                    >
                      Set currency
                    </Button>
                  </LockedUi>
                )}
              </p>
            </div>

            <div>
              <p className="t-text-text-30 t-text-body-sm t-m-0">
                Opening balance
              </p>
              <p className="t-text-text-100 t-text-body-sm t-m-0 t-flex t-items-center t-gap-1">
                {hasOpeningBalance && (
                  <span>
                    <AmountSuperScript
                      amount={Number(account.opening_balance)}
                    />
                  </span>
                )}

                <LockedUi feature={WRITE_PERMISSION_KEYS.DATA_SOURCES}>
                  <ConditionalToolTip
                    /* @tw */
                    propClass="t-w-64 !t-text-left react-datepicker-ignore-onclickoutside"
                    keepOpenOnActivation
                    condition={
                      bookkeepingSettings?.accounting_lock_date && (
                        <LockDateToolTipContent text="The starting date cannot be edited since the financials are locked." />
                      )
                    }
                  >
                    <span>
                      <Button
                        size="small"
                        customType={!hasOpeningBalance ? "ghost" : "ghost_icon"}
                        onClick={accountEdit.open}
                        disabled={Boolean(
                          bookkeepingSettings?.accounting_lock_date
                        )}
                      >
                        <span className="t-flex t-gap-1 t-items-center">
                          <span className="t-flex">
                            <PencilWithLine />
                          </span>
                          {!hasOpeningBalance && <span>Add</span>}
                        </span>
                      </Button>
                    </span>
                  </ConditionalToolTip>
                </LockedUi>
              </p>
            </div>

            <div>
              <p className="t-text-text-30 t-text-body-sm t-m-0">
                Opening balance date
              </p>
              <p className="t-text-text-100 t-text-body-sm t-m-0 t-flex t-gap-1 t-items-center">
                {account.opening_balance_date && (
                  <span>{formatDate(account.opening_balance_date)}</span>
                )}

                <LockedUi feature={WRITE_PERMISSION_KEYS.DATA_SOURCES}>
                  <ConditionalToolTip
                    /* @tw */
                    propClass="t-w-64 !t-text-left react-datepicker-ignore-onclickoutside"
                    keepOpenOnActivation
                    condition={
                      bookkeepingSettings?.accounting_lock_date && (
                        <LockDateToolTipContent text="The starting date cannot be edited since the financials are locked." />
                      )
                    }
                  >
                    <span>
                      <Button
                        disabled={Boolean(
                          bookkeepingSettings?.accounting_lock_date
                        )}
                        size="small"
                        customType={
                          !account.opening_balance_date ? "ghost" : "ghost_icon"
                        }
                        onClick={accountEdit.open}
                      >
                        <span className="t-flex t-gap-1 t-items-center">
                          <span className="t-flex">
                            <PencilWithLine />
                          </span>
                          {!account.opening_balance_date && <span>Add</span>}
                        </span>
                      </Button>
                    </span>
                  </ConditionalToolTip>
                </LockedUi>
              </p>
            </div>
            {account.status === BANK_ACCOUNT_STATUS.CLOSED &&
              account.closed_on && (
                <div>
                  <p className="t-text-text-30 t-text-body-sm t-m-0">
                    Closed on{" "}
                  </p>
                  <p className="t-text-text-100 t-text-body-sm t-m-0 t-flex t-items-center t-gap-1">
                    {dayjs(account.closed_on).format(DISPLAY_DATE)}
                  </p>
                </div>
              )}
          </div>
          <Divider />
          <div className="t-flex t-justify-end t-gap-1">
            <LockedUi feature={WRITE_PERMISSION_KEYS.DATA_SOURCES}>
              {account.status === BANK_ACCOUNT_STATUS.CLOSED ? (
                <Button
                  size="small"
                  customType="ghost"
                  onClick={markAccountActive}
                  disabled={isActivating}
                  isLoading={isActivating}
                >
                  Mark active
                </Button>
              ) : (
                <Modal.Root
                  open={markaccountCloseModal.isOpen}
                  onOpenChange={markaccountCloseModal.toggle}
                >
                  <Modal.Trigger asChild>
                    <Button size="small" customType="ghost">
                      Mark as closed
                    </Button>
                  </Modal.Trigger>
                  <Modal.Content>
                    <MarkAccountCloseModal
                      bankAccountId={account.uuid}
                      bankId={connection.uuid}
                      groupId={groupId}
                      entityId={entityId}
                      {...markaccountCloseModal}
                    />
                  </Modal.Content>
                </Modal.Root>
              )}
            </LockedUi>

            <Slider.Root>
              <Slider.Trigger asChild>
                <Button size="small">View Statements</Button>
              </Slider.Trigger>
              <Slider.Content>
                <BankStatementSlider
                  account={account}
                  connection={connection}
                />
              </Slider.Content>
            </Slider.Root>
          </div>
        </Accordion.Content>
      </Accordion.Item>

      {accountEdit.isOpen && (
        <EditOpeningBalance
          open={accountEdit.isOpen}
          onClose={accountEdit.close}
          bankAccount={account}
          connection={connection}
        />
      )}

      {assignCurrencyModal.isOpen && (
        <AssignCurrencyModal
          account={account}
          connection={connection}
          isOpen={assignCurrencyModal.isOpen}
          close={assignCurrencyModal.close}
        />
      )}
    </>
  );
};
